import { RenderClearFlag } from "../../../RenderEngine/RenderEnum/RenderClearFlag";
import { BaseCamera } from "../../../d3/core/BaseCamera";
import { ShadowCascadesMode } from "../../../d3/core/light/ShadowCascadesMode";
import { ShadowMode } from "../../../d3/core/light/ShadowMode";
import { ShadowUtils } from "../../../d3/core/light/ShadowUtils";
import { Scene3DShaderDeclaration } from "../../../d3/core/scene/Scene3DShaderDeclaration";
import { Plane } from "../../../d3/math/Plane";
import { ShadowCasterPass } from "../../../d3/shadowMap/ShadowCasterPass";
import { ShadowCullInfo, ShadowSliceData } from "../../../d3/shadowMap/ShadowSliceData";
import { LayaGL } from "../../../layagl/LayaGL";
import { StatElement } from "../../../layagl/StatisticsContext";
import { Color } from "../../../maths/Color";
import { MathUtils3D } from "../../../maths/MathUtils3D";
import { Matrix4x4 } from "../../../maths/Matrix4x4";
import { Vector3 } from "../../../maths/Vector3";
import { Vector4 } from "../../../maths/Vector4";
import { Viewport } from "../../../maths/Viewport";
import { Browser } from "../../../utils/Browser";
import { RenderCullUtil } from "../../DriverCommon/RenderCullUtil";
import { RenderListQueue } from "../../DriverCommon/RenderListQueue";
export class WebGLDirectLightShadowRP {
    get shadowCasterCommanBuffer() {
        return this._shadowCasterCommanBuffer;
    }
    set shadowCasterCommanBuffer(value) {
        this._shadowCasterCommanBuffer = value;
    }
    set light(value) {
        this._light = value;
        var lightWorld = Matrix4x4.TEMP;
        var lightWorldE = lightWorld.elements;
        var lightUp = this._lightup;
        var lightSide = this._lightSide;
        var lightForward = this._lightForward;
        Matrix4x4.createFromQuaternion(this._light.transform.rotation, lightWorld);
        lightSide.setValue(lightWorldE[0], lightWorldE[1], lightWorldE[2]);
        lightUp.setValue(lightWorldE[4], lightWorldE[5], lightWorldE[6]);
        lightForward.setValue(-lightWorldE[8], -lightWorldE[9], -lightWorldE[10]);
        var atlasResolution = this._light.shadowResolution;
        var cascadesMode = this.shadowCastMode = this._light.shadowCascadesMode;
        if (cascadesMode == ShadowCascadesMode.NoCascades) {
            this._cascadeCount = 1;
            this._shadowTileResolution = atlasResolution;
            this._shadowMapWidth = atlasResolution;
            this._shadowMapHeight = atlasResolution;
        }
        else {
            this._cascadeCount = cascadesMode == ShadowCascadesMode.TwoCascades ? 2 : 4;
            let shadowTileResolution = ShadowUtils.getMaxTileResolutionInAtlas(atlasResolution, atlasResolution, this._cascadeCount);
            this._shadowTileResolution = shadowTileResolution;
            this._shadowMapWidth = shadowTileResolution * 2;
            this._shadowMapHeight = cascadesMode == ShadowCascadesMode.TwoCascades ? shadowTileResolution : shadowTileResolution * 2;
        }
    }
    get light() {
        return this._light;
    }
    constructor() {
        this._cascadesSplitDistance = new Array(WebGLDirectLightShadowRP._maxCascades + 1);
        this._shadowMatrices = new Float32Array(16 * (WebGLDirectLightShadowRP._maxCascades));
        this._splitBoundSpheres = new Float32Array(WebGLDirectLightShadowRP._maxCascades * 4);
        this._shadowSliceDatas = [new ShadowSliceData(), new ShadowSliceData(), new ShadowSliceData(), new ShadowSliceData()];
        this._shadowMapSize = new Vector4();
        this._shadowBias = new Vector4();
        this._cascadeCount = 0;
        this._shadowMapWidth = 0;
        this._shadowMapHeight = 0;
        this._shadowTileResolution = 0;
        this._lightup = new Vector3();
        this._lightSide = new Vector3();
        this._lightForward = new Vector3();
        this._cascadesSplitDistance = new Array(WebGLDirectLightShadowRP._maxCascades + 1);
        this._renderQueue = new RenderListQueue(false);
        this._frustumPlanes = new Array(new Plane(new Vector3(), 0), new Plane(new Vector3(), 0), new Plane(new Vector3(), 0), new Plane(new Vector3(), 0), new Plane(new Vector3(), 0), new Plane(new Vector3(), 0));
        this._shadowCullInfo = new ShadowCullInfo();
    }
    update(context) {
        var splitDistance = this._cascadesSplitDistance;
        var frustumPlanes = this._frustumPlanes;
        var cameraNear = this.camera.nearplane;
        var shadowFar = Math.min(this.camera.farplane, this._light.shadowDistance);
        var shadowMatrices = this._shadowMatrices;
        var boundSpheres = this._splitBoundSpheres;
        ShadowUtils.getCascadesSplitDistance(this._light.shadowTwoCascadeSplits, this._light._shadowFourCascadeSplits, cameraNear, shadowFar, this.camera.fieldOfView * MathUtils3D.Deg2Rad, this.camera.aspectRatio, this.shadowCastMode, splitDistance);
        ShadowUtils.getCameraFrustumPlanes(this.camera._projectViewMatrix, frustumPlanes);
        var forward = Vector3.TEMP;
        this.camera.transform.getForward(forward);
        Vector3.normalize(forward, forward);
        for (var i = 0; i < this._cascadeCount; i++) {
            var sliceData = this._shadowSliceDatas[i];
            sliceData.sphereCenterZ = ShadowUtils.getBoundSphereByFrustum(splitDistance[i], splitDistance[i + 1], this.camera.fieldOfView * MathUtils3D.Deg2Rad, this.camera.aspectRatio, this.camera.transform.position, forward, sliceData.splitBoundSphere);
            ShadowUtils.getDirectionLightShadowCullPlanes(frustumPlanes, i, splitDistance, cameraNear, this._lightForward, sliceData);
            ShadowUtils.getDirectionalLightMatrices(this._lightup, this._lightSide, this._lightForward, i, this._light.shadowNearPlane, this._shadowTileResolution, sliceData, shadowMatrices);
            if (this._cascadeCount > 1)
                ShadowUtils.applySliceTransform(sliceData, this._shadowMapWidth, this._shadowMapHeight, i, shadowMatrices);
        }
        ShadowUtils.prepareShadowReceiverShaderValues(this._shadowMapWidth, this._shadowMapHeight, this._shadowSliceDatas, this._cascadeCount, this._shadowMapSize, shadowMatrices, boundSpheres);
    }
    render(context, list, count) {
        let shaderValues = context.sceneData;
        context.pipelineMode = "ShadowCaster";
        var shadowMap = this.destTarget;
        context.setRenderTarget(shadowMap, RenderClearFlag.Depth);
        context.setClearData(RenderClearFlag.Depth, Color.BLACK, 1, 0);
        let originCameraData = context.cameraData;
        for (var i = 0, n = this._cascadeCount; i < n; i++) {
            var sliceData = this._shadowSliceDatas[i];
            this.getShadowBias(sliceData.projectionMatrix, sliceData.resolution, this._shadowBias);
            this._setupShadowCasterShaderValues(shaderValues, sliceData, this._lightForward, this._shadowBias);
            var shadowCullInfo = this._shadowCullInfo;
            shadowCullInfo.position = sliceData.position;
            shadowCullInfo.cullPlanes = sliceData.cullPlanes;
            shadowCullInfo.cullPlaneCount = sliceData.cullPlaneCount;
            shadowCullInfo.cullSphere = sliceData.splitBoundSphere;
            shadowCullInfo.direction = this._lightForward;
            var time = Browser.now();
            RenderCullUtil.cullDirectLightShadow(shadowCullInfo, list, count, this._renderQueue, context);
            LayaGL.statAgent.recordTimeData(StatElement.T_CullShadow, Browser.now() - time);
            context.cameraData = sliceData.cameraShaderValue;
            context.cameraUpdateMask++;
            var resolution = sliceData.resolution;
            var offsetX = sliceData.offsetX;
            var offsetY = sliceData.offsetY;
            if (this._renderQueue.elements.length > 0) {
                Viewport.TEMP.set(offsetX, offsetY, resolution, resolution);
                Vector4.TEMP.setValue(offsetX + 1, offsetY + 1, resolution - 2, resolution - 2);
                context.setViewPort(Viewport.TEMP);
                context.setScissor(Vector4.TEMP);
            }
            else {
                Viewport.TEMP.set(offsetX, offsetY, resolution, resolution);
                context.setViewPort(Viewport.TEMP);
                Vector4.TEMP.setValue(offsetX, offsetY, resolution, resolution);
                context.setScissor(Vector4.TEMP);
            }
            context.setClearData(RenderClearFlag.Depth, Color.BLACK, 1, 0);
            this._renderQueue.renderQueue(context);
            LayaGL.statAgent.recordCTData(StatElement.CT_ShadowDrawCall, this._renderQueue.elements.length);
            this._applyCasterPassCommandBuffer(context);
        }
        this._applyRenderData(context.sceneData, context.cameraData);
        this._renderQueue._batch.recoverData();
        context.cameraData = originCameraData;
        context.cameraUpdateMask++;
    }
    _applyRenderData(scene, camera) {
        var light = this._light;
        if (light.shadowCascadesMode !== ShadowCascadesMode.NoCascades)
            scene.addDefine(Scene3DShaderDeclaration.SHADERDEFINE_SHADOW_CASCADE);
        else
            scene.removeDefine(Scene3DShaderDeclaration.SHADERDEFINE_SHADOW_CASCADE);
        switch (light.shadowMode) {
            case ShadowMode.Hard:
                scene.removeDefine(Scene3DShaderDeclaration.SHADERDEFINE_SHADOW_SOFT_SHADOW_LOW);
                scene.removeDefine(Scene3DShaderDeclaration.SHADERDEFINE_SHADOW_SOFT_SHADOW_HIGH);
                break;
            case ShadowMode.SoftLow:
                scene.addDefine(Scene3DShaderDeclaration.SHADERDEFINE_SHADOW_SOFT_SHADOW_LOW);
                scene.removeDefine(Scene3DShaderDeclaration.SHADERDEFINE_SHADOW_SOFT_SHADOW_HIGH);
                break;
            case ShadowMode.SoftHigh:
                scene.addDefine(Scene3DShaderDeclaration.SHADERDEFINE_SHADOW_SOFT_SHADOW_HIGH);
                scene.removeDefine(Scene3DShaderDeclaration.SHADERDEFINE_SHADOW_SOFT_SHADOW_LOW);
                break;
        }
        scene.setBuffer(ShadowCasterPass.SHADOW_MATRICES, this._shadowMatrices);
        scene.setVector(ShadowCasterPass.SHADOW_MAP_SIZE, this._shadowMapSize);
        scene.setBuffer(ShadowCasterPass.SHADOW_SPLIT_SPHERES, this._splitBoundSpheres);
    }
    _applyCasterPassCommandBuffer(context) {
        if (!this.shadowCasterCommanBuffer || this.shadowCasterCommanBuffer.length == 0)
            return;
        this.shadowCasterCommanBuffer.forEach(function (value) {
            value._apply();
        });
    }
    getShadowBias(shadowProjectionMatrix, shadowResolution, out) {
        var frustumSize;
        frustumSize = 2.0 / shadowProjectionMatrix.elements[0];
        var texelSize = frustumSize / shadowResolution;
        var depthBias = -this._light.shadowDepthBias * texelSize;
        var normalBias = -this._light.shadowNormalBias * texelSize;
        if (this._light.shadowMode == ShadowMode.SoftHigh) {
            const kernelRadius = 2.5;
            depthBias *= kernelRadius;
            normalBias *= kernelRadius;
        }
        out.setValue(depthBias, normalBias, 0.0, 0.0);
    }
    _setupShadowCasterShaderValues(shaderValues, shadowSliceData, LightParam, shadowBias) {
        shaderValues.setVector(ShadowCasterPass.SHADOW_BIAS, shadowBias);
        shaderValues.setVector3(ShadowCasterPass.SHADOW_LIGHT_DIRECTION, LightParam);
        var cameraSV = shadowSliceData.cameraShaderValue;
        cameraSV.setMatrix4x4(BaseCamera.VIEWMATRIX, shadowSliceData.viewMatrix);
        cameraSV.setMatrix4x4(BaseCamera.PROJECTMATRIX, shadowSliceData.projectionMatrix);
        cameraSV.setMatrix4x4(BaseCamera.VIEWPROJECTMATRIX, shadowSliceData.viewProjectMatrix);
        shaderValues.setMatrix4x4(BaseCamera.VIEWPROJECTMATRIX, shadowSliceData.viewProjectMatrix);
    }
    destroy() {
        for (var i = 0; i < this._shadowSliceDatas.length; i++) {
            this._shadowSliceDatas[i].destroy();
        }
        this._renderQueue.destroy();
        this._cascadesSplitDistance = null;
        this._frustumPlanes = null;
        this._shadowMatrices = null;
        this._splitBoundSpheres = null;
    }
}
WebGLDirectLightShadowRP._maxCascades = 4;
